import {
  Grid,
  Stack,
  Box,
  Typography,
  FormControl,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableBody,
  MenuItem,
  CircularProgress,
  Tooltip,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Popover,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useMediaQuery,
  useTheme,
  Chip,
  Divider,
} from "@mui/material";
import React, { useEffect } from "react";
import { getApiBaseUrl } from "../../../helpers/apiUrl";
import {
  FilledButton,
} from "../../customComponents/styled/styledButtons";
import SaveIcon from "@mui/icons-material/Save";
import DeleteIcon from "@mui/icons-material/Delete";
import { StyledPaper } from "../../customComponents/styled/styledPaper";
import {
  StyledSelect,
  StyledTextArea,
  StyledTextField,
} from "../../customComponents/styled/styledInputs";
import {
  StyledTableCell,
  StyledTableRow,
} from "../../customComponents/styled/styledTable";
import { useGetClassesQuery, useGetClassroomWithSubjectsQuery } from "../classroom/classApiSlice";
import {
  useCreateCommentMutation,
  useCreateStudentTraitsMutation,
  useDeleteCommentMutation,
  useDeleteStudentTraitsMutation,
  useGetCommentByStudentQuery,
  useGetCommentBySubjectAndClassroomQuery,
  useGetRecordsBySubjectAndClassQuery,
  useGetStudentTraitsQuery,
} from "../quick-entry/recordApiSlice";
import { useGetGradesQuery } from "../settings/grades/gradesApiSlice";
import {
  useGetCurrentTermQuery,
  useGetSessionQuery,
} from "../settings/session/sessionApiSlice";
import { useGetTraitsForClassroomQuery } from "../settings/traits/traitsApiSlice";
import toast from "../../../helpers/toast";
import ensureLightReportPreviewHtml, {
  REPORT_PREVIEW_WIDTH,
  printReportPreviewHtml,
} from "../../../helpers/ensureLightReportPreviewHtml";
import CustomWarningDialog from "../../customComponents/CustomWarningDialog";
import { useGetSubSectionQuery, useGetSectionQuery } from "../settings/sections/sectionsApiSlice";
import { useGetDepartmentQuery } from "../settings/departments/departmentsApiSlice";
import { Print, Close as CloseIcon, Add as AddIcon, AutoAwesome as AutoAwesomeIcon, ExpandMore as ExpandMoreIcon } from "@mui/icons-material";
import { useCreateStudentReportMutation } from "../student-report/studentReportApiSlice";
import { useGetTemplatesQuery } from "../settings/sections/sectionsApiSlice";
import { is } from "date-fns/locale";
import AICommentModal from "../../customComponents/AICommentModal";

const SubjectTableRow = ({
  subject,
  classId,
  student,
  assessments,
  grades,
  type = "term",
  isCommentBased = false,
}) => {
  const skipSubjectQueries = !subject?.id || !classId;

  const { data: records } = useGetRecordsBySubjectAndClassQuery(
    { subject_id: subject?.id, classroom_id: classId },
    { skip: skipSubjectQueries }
  );

  const { data: comments } = useGetCommentBySubjectAndClassroomQuery(
    { subject_id: subject?.id, classroom_id: classId, type },
    { skip: skipSubjectQueries }
  );

  const studentRecords = records?.filter((r) => r.student_id === student.id);
  const studentSubjectRecord = studentRecords?.filter(
    (r) => r.subject_id === subject.id
  );
  const studentSubjectComment = comments?.find(
    (c) => c.student_id === student.id && c.subject_id === subject.id
  );

  if (type === "midterm" || isCommentBased) {
    return (
      <StyledTableRow>
        <StyledTableCell component="th" scope="row">
          {subject?.name}
        </StyledTableCell>
        <StyledTableCell>{studentSubjectComment?.content || "—"}</StyledTableCell>
      </StyledTableRow>
    );
  }

  const totalScore = studentSubjectRecord?.reduce(
    (acc, curr) => acc + curr.score,
    0
  );

  // match grade to total score
  const grade = grades?.find(
    (g) => g.min_score <= totalScore && g.max_score >= totalScore
  );

  return (
    <StyledTableRow>
      <StyledTableCell component="th" scope="row">
        {subject?.name}
      </StyledTableCell>
      {/* match record against assessments */}
      {assessments?.map((assessment) => {
        const record = studentSubjectRecord?.find(
          (r) => r.assessment_id === assessment.id
        );
        return (
          <StyledTableCell key={assessment.id} component="th" scope="row">
            {record?.score || 0}
          </StyledTableCell>
        );
      })}
      <StyledTableCell>{totalScore || 0}</StyledTableCell>
      <StyledTableCell>{grade?.name}</StyledTableCell>
      <StyledTableCell>{studentSubjectComment?.content || grade?.description}</StyledTableCell>
    </StyledTableRow>
  );
};

const SubjectMobileCard = ({
  subject,
  classId,
  student,
  assessments,
  grades,
}) => {
  const skipSubjectQueries = !subject?.id || !classId;

  const { data: records } = useGetRecordsBySubjectAndClassQuery(
    { subject_id: subject?.id, classroom_id: classId },
    { skip: skipSubjectQueries }
  );

  const { data: comments } = useGetCommentBySubjectAndClassroomQuery(
    { subject_id: subject?.id, classroom_id: classId, type: "term" },
    { skip: skipSubjectQueries }
  );

  const studentRecords = records?.filter((r) => r.student_id === student.id);
  const studentSubjectRecord = studentRecords?.filter(
    (r) => r.subject_id === subject.id
  );
  const studentSubjectComment = comments?.find(
    (c) => c.student_id === student.id && c.subject_id === subject.id
  );

  const totalScore = studentSubjectRecord?.reduce(
    (acc, curr) => acc + curr.score,
    0
  ) || 0;

  const grade = grades?.find(
    (g) => g.min_score <= totalScore && g.max_score >= totalScore
  );

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        p: 1.5,
        mb: 1.5,
      }}
    >
      <Typography variant="subtitle2" sx={{ mb: 1, color: "primary.main" }}>
        {subject?.name}
      </Typography>

      {(assessments || []).map((assessment) => {
        const record = studentSubjectRecord?.find(
          (r) => r.assessment_id === assessment.id
        );
        return (
          <Box
            key={assessment.id}
            sx={{ display: "flex", justifyContent: "space-between", py: 0.4 }}
          >
            <Typography variant="body2" color="text.secondary">
              {assessment.name} ({assessment.obtainable_score})
            </Typography>
            <Typography variant="body2">{record?.score || 0}</Typography>
          </Box>
        );
      })}

      <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.4, mt: 0.5 }}>
        <Typography variant="body2" color="text.secondary">Total</Typography>
        <Typography variant="body2">{totalScore}</Typography>
      </Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.4 }}>
        <Typography variant="body2" color="text.secondary">Grade</Typography>
        <Typography variant="body2">{grade?.name || "-"}</Typography>
      </Box>
      <Box sx={{ mt: 0.6 }}>
        <Typography variant="body2" color="text.secondary">Remark</Typography>
        <Typography variant="body2">{studentSubjectComment?.content || grade?.description || "-"}</Typography>
      </Box>
    </Box>
  );
};

const SubjectMidtermMobileCard = ({
  subject,
  classId,
  student,
}) => {
  const skipSubjectQueries = !subject?.id || !classId;

  const { data: comments } = useGetCommentBySubjectAndClassroomQuery(
    { subject_id: subject?.id, classroom_id: classId, type: "midterm" },
    { skip: skipSubjectQueries }
  );

  const studentSubjectComment = comments?.find(
    (c) => c.student_id === student.id && c.subject_id === subject.id
  );

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        p: 1.5,
        mb: 1.5,
      }}
    >
      <Typography variant="subtitle2" sx={{ mb: 1, color: "primary.main" }}>
        {subject?.name}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.4 }}>
        Midterm remark
      </Typography>
      <Typography variant="body2">
        {studentSubjectComment?.content || "—"}
      </Typography>
    </Box>
  );
};

const normalizeCommentTitle = (title) => String(title || "").trim().replace(/\s+/g, " ");

const getCommentTitleKey = (title) => normalizeCommentTitle(title).toLowerCase();

const dedupeCommentTitles = (titles) => {
  const seen = new Set();
  const result = [];
  for (const title of titles || []) {
    const normalizedTitle = normalizeCommentTitle(title);
    const key = normalizedTitle.toLowerCase();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    result.push(normalizedTitle);
  }
  return result;
};

const getStoredCommentTitles = (classId, type = "term") => {
  try {
    const stored = localStorage.getItem(`custom_comment_titles_${type}_${classId}`);
    return dedupeCommentTitles(stored ? JSON.parse(stored) : []);
  } catch {
    return [];
  }
};

const saveCommentTitlesToStorage = (classId, titles, type = "term") => {
  try {
    localStorage.setItem(`custom_comment_titles_${type}_${classId}`, JSON.stringify(dedupeCommentTitles(titles)));
  } catch {
    // ignore storage errors
  }
};

const dedupeCustomComments = (commentRows) => {
  const byTitle = new Map();

  for (const row of commentRows || []) {
    const title = normalizeCommentTitle(row?.title);
    const key = title.toLowerCase();
    if (!key) continue;

    const content = row?.content || "";
    const existing = byTitle.get(key);
    if (!existing) {
      byTitle.set(key, { title, content });
      continue;
    }

    if (!existing.content && content) {
      byTitle.set(key, { title: existing.title || title, content });
    }
  }

  return Array.from(byTitle.values());
};

const mergeCustomCommentsWithStoredTitles = (customComments, storedTitles) => {
  const mergedComments = dedupeCustomComments(customComments);
  const existingTitleSet = new Set(mergedComments.map((c) => getCommentTitleKey(c.title)));

  for (const title of dedupeCommentTitles(storedTitles)) {
    const key = getCommentTitleKey(title);
    if (!existingTitleSet.has(key)) {
      mergedComments.push({ title, content: "" });
      existingTitleSet.add(key);
    }
  }

  return mergedComments;
};

const persistCustomCommentTitles = (classId, comments, type) => {
  saveCommentTitlesToStorage(classId, (comments || []).map((c) => c.title), type);
};

const removeStoredCommentTitle = (classId, title, type) => {
  const titleKey = getCommentTitleKey(title);
  const nextTitles = getStoredCommentTitles(classId, type).filter(
    (storedTitle) => getCommentTitleKey(storedTitle) !== titleKey
  );
  saveCommentTitlesToStorage(classId, nextTitles, type);
};

function ExamsScoreSheet({ student, classId, assessments, isSectionHead, sectionId, isDepartmentHead, departmentId }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isAnyHead = isSectionHead || isDepartmentHead;
  const [activeTab, setActiveTab] = React.useState(0);
  const [traitsRecord, setTraitsRecord] = React.useState([]);
  const [teacherComment, setTeacherComment] = React.useState("");
  const [principalComment, setPrincipalComment] = React.useState("");
  const [departmentComments, setDepartmentComments] = React.useState({});
  const [midtermTeacherComment, setMidtermTeacherComment] = React.useState("");
  const [midtermPrincipalComment, setMidtermPrincipalComment] = React.useState("");
  const [midtermDepartmentComments, setMidtermDepartmentComments] = React.useState({});
  const [customMidtermComments, setCustomMidtermComments] = React.useState([]);
  const [customTermComments, setCustomTermComments] = React.useState([]);
  const [openWarning, setOpenWarning] = React.useState(false);
  const [pendingDeleteComment, setPendingDeleteComment] = React.useState(null); // { type: 'term'|'midterm', idx: number }
  const [isPrintingReport, setIsPringtingReport] = React.useState(false);
  const [activeTemplate, setActiveTemplate] = React.useState(null);
  const [openPreview, setOpenPreview] = React.useState(false);
  const [previewHtml, setPreviewHtml] = React.useState("");
  const previewIframeRef = React.useRef(null);
  const [previewIframeHeight, setPreviewIframeHeight] = React.useState(900);
  const [templateAnchorEl, setTemplateAnchorEl] = React.useState(null);
  const [openAiComment, setOpenAiComment] = React.useState(false);
  const handleSaveRef = React.useRef(null);

  const { data: classroom, isFetching: isLoadingClassroom } =
    useGetClassroomWithSubjectsQuery(classId); //to get updated list of classrooms with subjects
  const skipSubsectionQueries = !classroom?.subsection_id;
  const skipStudentQueries = !student?.id;

  const { data: commentGrades, isFetching: isLoadingCommentGrades } =
    useGetGradesQuery(`all/comment/${classroom?.subsection_id}`, { skip: skipSubsectionQueries });
  const { data: scoreGrades, isLoadingScores } = useGetGradesQuery(
    `all/score/${classroom?.subsection_id}`,
    { skip: skipSubsectionQueries }
  );
  const { data: session, isFetching: isLoadingSession } =
    useGetSessionQuery("current");
  const { data: term, isFetching: isLoadingTerm } = useGetCurrentTermQuery();
  const { data: traits, isFetching: isLoadingTraits } = useGetTraitsForClassroomQuery(
    classId,
    { skip: !classId }
  );
  const { data: studentTraits, isFetching: isLoadingStudentTraits } =
    useGetStudentTraitsQuery(
      { student_id: student?.id, classroom_id: classId },
      { skip: skipStudentQueries }
    );
  const { data: comments, isFetching: isLoadingComment } =
    useGetCommentByStudentQuery({ student_id: student?.id, type: "term" }, { skip: skipStudentQueries });
  const { data: midtermComments, isFetching: isLoadingMidtermComment } =
    useGetCommentByStudentQuery({ student_id: student?.id, type: "midterm" }, { skip: skipStudentQueries });

  const { data: subsection, isFetching: isLoadingSubsection } =
    useGetSubSectionQuery(classroom?.subsection_id, { skip: skipSubsectionQueries });
  const effectiveSectionId = sectionId || subsection?.section_id;
  const { data: section } = useGetSectionQuery(effectiveSectionId, { skip: !effectiveSectionId });
  const { data: allDepartments } = useGetDepartmentQuery("all");

  const sectionCommentTitle = section?.head_title ? `${section.head_title}'s Comment` : "Section Comment";
  const deptCommentTitle = (dept) => dept?.head_title ? `${dept.head_title}'s Comment` : "Department Comment";

  const isCommentBased = subsection?.report_type === 'skill';

  const [saveTraits, { isLoading: saveTraitsLoading }] =
    useCreateStudentTraitsMutation();
  const [deleteTraits, { isLoading: deleteTraitsLoading }] =
    useDeleteStudentTraitsMutation();
  const [saveComment, { isLoading: saveCommentLoading }] =
    useCreateCommentMutation();
  const [deleteComment, { isLoading: deleteCommentLoading }] =
    useDeleteCommentMutation();


  const [createStudentReport, { isLoading: createStudentReportLoading }] =
    useCreateStudentReportMutation();

  // Term: traits + form-teacher/principal comments
  useEffect(() => {
    const returnedTraits = studentTraits
      ?.filter((t) => t.term_id === term?.id)
      ?.map((t) => ({
        trait_id: t.trait_id,
        behavior_id: t.behavior_id,
        grade_id: t.grade_id,
      }));
    if (returnedTraits) {
      setTraitsRecord(returnedTraits);
    } else {
      setTraitsRecord([]);
    }

    const returnedPrincipalComment = comments?.find(
      (item) =>
        item.term_id === term?.id &&
        item.section_id !== null &&
        item?.title?.toLowerCase() === sectionCommentTitle.toLowerCase()
    )?.content;

    const returnedFormTeacherComment = comments?.find(
      (item) =>
        item.term_id === term?.id &&
        item?.title?.toLowerCase() === "classroom comment"
    )?.content;

    if (returnedFormTeacherComment) {
      setTeacherComment(returnedFormTeacherComment);
    } else {
      setTeacherComment("");
    }

    if (returnedPrincipalComment) {
      setPrincipalComment(returnedPrincipalComment);
    } else {
      setPrincipalComment("");
    }

    const deptMap = {};
    comments?.filter(
      (item) =>
        item.term_id === term?.id &&
        item.department_id !== null
    ).forEach((item) => {
      const dept = allDepartments?.find((d) => d.id === item.department_id);
      const expectedTitle = deptCommentTitle(dept);
      if (item?.title?.toLowerCase() === expectedTitle.toLowerCase()) {
        deptMap[item.department_id] = item.content;
      }
    });
    setDepartmentComments(deptMap);

    const knownTermTitles = ["classroom comment", getCommentTitleKey(sectionCommentTitle)];
    allDepartments?.forEach((d) => knownTermTitles.push(getCommentTitleKey(deptCommentTitle(d))));
    const returnedCustomTermComments = comments
      ?.filter(
        (item) =>
          item.term_id === term?.id &&
          item.subject_id === null &&
          !knownTermTitles.includes(getCommentTitleKey(item.title))
      )
      .map((item) => ({ title: item.title, content: item.content })) || [];

    // Merge with class-level stored titles so previously-used titles appear for new students
    const storedTermTitles = getStoredCommentTitles(classId, "term");
    setCustomTermComments(mergeCustomCommentsWithStoredTitles(returnedCustomTermComments, storedTermTitles));
  }, [studentTraits, term, comments, section, allDepartments, classId]);

  // Midterm: form-teacher/principal midterm comments
  useEffect(() => {
    if (!midtermComments) return;

    const returnedMidtermTeacherComment = midtermComments?.find(
      (item) =>
        item.term_id === term?.id &&
        item?.title?.toLowerCase() === "classroom comment"
    )?.content;

    const returnedMidtermPrincipalComment = midtermComments?.find(
      (item) =>
        item.term_id === term?.id &&
        item.section_id !== null &&
        item?.title?.toLowerCase() === sectionCommentTitle.toLowerCase()
    )?.content;

    setMidtermTeacherComment(returnedMidtermTeacherComment || "");
    setMidtermPrincipalComment(returnedMidtermPrincipalComment || "");

    const midtermDeptMap = {};
    midtermComments?.filter(
      (item) =>
        item.term_id === term?.id &&
        item.department_id !== null
    ).forEach((item) => {
      const dept = allDepartments?.find((d) => d.id === item.department_id);
      const expectedTitle = deptCommentTitle(dept);
      if (item?.title?.toLowerCase() === expectedTitle.toLowerCase()) {
        midtermDeptMap[item.department_id] = item.content;
      }
    });
    setMidtermDepartmentComments(midtermDeptMap);

    const knownTitles = ["classroom comment", getCommentTitleKey(sectionCommentTitle)];
    allDepartments?.forEach((d) => knownTitles.push(getCommentTitleKey(deptCommentTitle(d))));
    const returnedCustomComments = midtermComments
      ?.filter(
        (item) =>
          item.term_id === term?.id &&
          item.subject_id === null &&
          !knownTitles.includes(getCommentTitleKey(item.title))
      )
      .map((item) => ({ title: item.title, content: item.content })) || [];

    // Merge with class-level stored titles so previously-used titles appear for new students
    const storedMidtermTitles = getStoredCommentTitles(classId, "midterm");
    setCustomMidtermComments(mergeCustomCommentsWithStoredTitles(returnedCustomComments, storedMidtermTitles));
  }, [term, midtermComments, section, allDepartments, classId]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "s") {
        e.preventDefault();
        handleSaveRef.current?.();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);


  // traits is already filtered for this classroom by the backend (classroom-specific or subsection fallback)
  const subsectionTraits = traits;

  const handleTraitGradeChange = (e, traitId, behaviorId) => {
    const item = {
      trait_id: traitId,
      behavior_id: behaviorId,
      grade_id: e.target.value,
    };

    const newTraitsRecord = [...traitsRecord];
    const index = newTraitsRecord?.findIndex(
      (t) => t.trait_id === traitId && t.behavior_id === behaviorId
    );
    if (index === -1) {
      newTraitsRecord.push(item);
    } else {
      newTraitsRecord[index] = item;
    }
    setTraitsRecord(newTraitsRecord);
  };

  const handleSave = async () => {
    if (activeTab === 1) {
      const midtermCustomCommentsToSave = dedupeCustomComments(customMidtermComments);
      // Midterm tab: save only midterm comment
      let midtermCommentBody;
      if (isDepartmentHead) {
        midtermCommentBody = {
          classroom_id: null,
          section_id: null,
          department_id: parseInt(departmentId),
          type: "midterm",
          comments: [{
            student_id: student.id,
            title: deptCommentTitle(allDepartments?.find((d) => d.id === parseInt(departmentId))),
            content: midtermDepartmentComments[parseInt(departmentId)] || "",
          }],
        };
      } else if (isSectionHead) {
        midtermCommentBody = {
          classroom_id: null,
          section_id: parseInt(sectionId),
          department_id: null,
          type: "midterm",
          comments: [{
            student_id: student.id,
            title: sectionCommentTitle,
            content: midtermPrincipalComment,
          }],
        };
      } else {
        persistCustomCommentTitles(classId, midtermCustomCommentsToSave, "midterm");
        setCustomMidtermComments(midtermCustomCommentsToSave);
        midtermCommentBody = {
          classroom_id: parseInt(classId),
          section_id: null,
          department_id: null,
          type: "midterm",
          comments: [
            {
              student_id: student.id,
              title: "Classroom Comment",
              content: midtermTeacherComment,
            },
            ...midtermCustomCommentsToSave
              .map((c) => ({
                student_id: student.id,
                title: c.title,
                content: c.content,
              })),
          ],
        };
      }
      try {
        const commentResponse = await saveComment(midtermCommentBody).unwrap();
        if (commentResponse) {
          toast.success("Midterm comment saved successfully");
        }
      } catch (error) {
        toast.error(error?.message || "Something went wrong");
      }
      return;
    }

    // Term tab
    const traitsBody = {
      classroom_id: parseInt(classId),
      student_id: student.id,
      grades: traitsRecord,
    };

    let commentBody;
    const termCustomCommentsToSave = dedupeCustomComments(customTermComments);
    if (isDepartmentHead) {
      commentBody = {
        classroom_id: null,
        section_id: null,
        department_id: parseInt(departmentId),
        type: "term",
        comments: [{
          student_id: student.id,
          title: deptCommentTitle(allDepartments?.find((d) => d.id === parseInt(departmentId))),
          content: departmentComments[parseInt(departmentId)] || "",
        }],
      };
    } else if (isSectionHead) {
      commentBody = {
        classroom_id: null,
        section_id: parseInt(sectionId),
        department_id: null,
        type: "term",
        comments: [{
          student_id: student.id,
          title: sectionCommentTitle,
          content: principalComment,
        }],
      };
    } else {
      commentBody = {
        classroom_id: parseInt(classId),
        section_id: null,
        department_id: null,
        type: "term",
        comments: [
          {
            student_id: student.id,
            title: "Classroom Comment",
            content: teacherComment,
          },
          ...termCustomCommentsToSave
            .map((c) => ({
              student_id: student.id,
              title: c.title,
              content: c.content,
            })),
        ],
      };
    }

    try {
      if (!isAnyHead) {
        persistCustomCommentTitles(classId, termCustomCommentsToSave, "term");
        setCustomTermComments(termCustomCommentsToSave);
        //save traits, only for teachers
        const traitResponse = await saveTraits(traitsBody).unwrap();
        if (traitResponse) {
          toast.success("Traits saved successfully");
        }
      }

      //save comment, for both teachers and section heads
      const commentResponse = await saveComment(commentBody).unwrap();

      if (commentResponse) {
        toast.success("Comment saved successfully");
      }
    } catch (error) {
      toast.error(error?.message || "Something went wrong");
    }
  };

  handleSaveRef.current = handleSave;

  const handleClickDelete = () => {
    handleDeleteComment();
    handleDeleteTraits();
  };

  const handleDeleteComment = async () => {
    const body = {
      student_id: student.id,
      classroom_id: isAnyHead ? null : parseInt(classId),
      section_id: isSectionHead ? parseInt(sectionId) : null,
      department_id: isDepartmentHead ? parseInt(departmentId) : null,
      type: activeTab === 1 ? "midterm" : "term",
    };

    try {
      const response = await deleteComment(body).unwrap();

      if (response) {
        toast.success("Comment deleted successfully");
      }
    } catch (error) {
      if (error.status === 404) {
        toast.error(error?.message || "No record found");
      } else {
        toast.error(error?.message || "Something went wrong");
      }
    }
  };

  const handleDeleteTraits = async () => {
    const body = {
      student_id: student.id,
      classroom_id: parseInt(classId),
    };

    try {
      const traitResponse = await deleteTraits(body).unwrap();
      if (traitResponse) {
        toast.success("Traits deleted successfully");
      }
    } catch (error) {
      if (error.status === 404) {
        toast.error(error?.message || "No record found");
      } else {
        toast.error(error?.message || "Something went wrong");
      }
    }
  };

  const removeCustomCommentFromState = (type, idx) => {
    if (type === "term") {
      setCustomTermComments((prev) => {
        const nextComments = prev.filter((_, rowIdx) => rowIdx !== idx);
        persistCustomCommentTitles(classId, nextComments, "term");
        return nextComments;
      });
      return;
    }

    setCustomMidtermComments((prev) => {
      const nextComments = prev.filter((_, rowIdx) => rowIdx !== idx);
      persistCustomCommentTitles(classId, nextComments, "midterm");
      return nextComments;
    });
  };

  const restoreCustomCommentToState = (type, idx, comment) => {
    if (!comment) return;

    if (type === "term") {
      setCustomTermComments((prev) => {
        const nextComments = [...prev];
        nextComments.splice(Math.min(idx, nextComments.length), 0, comment);
        persistCustomCommentTitles(classId, nextComments, "term");
        return nextComments;
      });
      return;
    }

    setCustomMidtermComments((prev) => {
      const nextComments = [...prev];
      nextComments.splice(Math.min(idx, nextComments.length), 0, comment);
      persistCustomCommentTitles(classId, nextComments, "midterm");
      return nextComments;
    });
  };

  const handleDeleteCustomComment = async () => {
    if (!pendingDeleteComment) return;

    const { type, idx } = pendingDeleteComment;
    const deletedComment = type === "term" ? customTermComments[idx] : customMidtermComments[idx];
    const title = normalizeCommentTitle(deletedComment?.title);

    if (!title) {
      removeCustomCommentFromState(type, idx);
      setPendingDeleteComment(null);
      return;
    }

    removeStoredCommentTitle(classId, title, type);
    removeCustomCommentFromState(type, idx);

    try {
      await deleteComment({
        student_id: student.id,
        classroom_id: parseInt(classId),
        section_id: null,
        department_id: null,
        type,
        title,
      }).unwrap();
      toast.success("Comment field deleted successfully");
    } catch (error) {
      if (error?.status === 404) {
        return;
      }

      restoreCustomCommentToState(type, idx, deletedComment);
      toast.error(error?.message || "Could not delete comment field");
    } finally {
      setPendingDeleteComment(null);
    }
  };

  const handlePrint = async (template) => {
    try {
      setIsPringtingReport(true);
      const data = {
        student_id: student?.id,
        term_id: term?.id,
        classroom_id: parseInt(classId),
        template_id: template?.id,
        subsection_id: classroom?.subsection_id,
      };
      const BASE_URL = getApiBaseUrl();
      const token = localStorage
        .getItem("access_token")
        .replace('"', "")
        .replace('"', "");
      const url = `/report/student?preview=html&student_id=${data.student_id}&term_id=${data.term_id}&classroom_id=${data.classroom_id}&template_id=${data.template_id}&subsection_id=${data.subsection_id}`;
      const req = await fetch(BASE_URL + url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setIsPringtingReport(false);
      if (!req.ok) {
        return toast.error("Error printing result");
      }
      const htmlText = await req.text();
      setPreviewIframeHeight(900);
      setPreviewHtml(ensureLightReportPreviewHtml(htmlText));
      setOpenPreview(true);
    } catch (error) {
      setIsPringtingReport(false);
      toast.error("Error printing result");
    }
  };

  const handlePrintPreview = () => {
    if (!previewHtml) {
      toast.info("Load a report preview before printing.");
      return;
    }

    const studentName = `${student?.first_name || ""} ${student?.last_name || ""}`.trim() || "Student";
    const didOpen = printReportPreviewHtml(previewHtml, `${studentName} Report Preview`);
    if (!didOpen) {
      toast.error("Unable to open print preview. Please allow pop-ups and try again.");
    }
  };

  const measurePreviewIframeHeight = () => {
    const iframe = previewIframeRef.current;
    if (!iframe) return;

    try {
      const doc = iframe.contentDocument || iframe.contentWindow?.document;
      if (!doc) return;

      const body = doc.body;
      const html = doc.documentElement;
      const rawHeight = Math.max(
        body?.scrollHeight || 0,
        body?.offsetHeight || 0,
        html?.clientHeight || 0,
        html?.scrollHeight || 0,
        html?.offsetHeight || 0
      );

      if (rawHeight > 0) {
        const nextHeight = Math.ceil(rawHeight) + 8;
        setPreviewIframeHeight((prev) => (prev === nextHeight ? prev : nextHeight));
      }
    } catch {
      // keep the fallback height if the iframe cannot be measured
    }
  };

  const handlePreviewIframeLoad = () => {
    measurePreviewIframeHeight();
    window.setTimeout(measurePreviewIframeHeight, 50);
    window.setTimeout(measurePreviewIframeHeight, 300);
    window.setTimeout(measurePreviewIframeHeight, 850);
    window.setTimeout(measurePreviewIframeHeight, 1500);
  };

  return (
    <Grid container columnSpacing={3}>
      {/* modals */}
      <Dialog
        open={openPreview}
        onClose={() => setOpenPreview(false)}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{ sx: { height: isMobile ? "100%" : "90vh", display: "flex", flexDirection: "column" } }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 1,
          }}
        >
          Student Report Preview
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Tooltip title="Print report">
              <span>
                <IconButton onClick={handlePrintPreview} disabled={!previewHtml}>
                  <Print />
                </IconButton>
              </span>
            </Tooltip>
            <IconButton onClick={() => setOpenPreview(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0, overflow: "auto", bgcolor: "#fff", flex: 1, minHeight: 0 }}>
          <Box sx={{ width: REPORT_PREVIEW_WIDTH, minWidth: REPORT_PREVIEW_WIDTH, mx: "auto" }}>
            <iframe
              ref={previewIframeRef}
              title="Student Report Preview"
              srcDoc={previewHtml}
              onLoad={handlePreviewIframeLoad}
              scrolling="no"
              style={{
                width: "100%",
                height: previewIframeHeight,
                border: "none",
                backgroundColor: "#fff",
                display: "block",
              }}
            />
          </Box>
        </DialogContent>
      </Dialog>
      <CustomWarningDialog
        open={openWarning}
        setOpen={setOpenWarning}
        action={handleClickDelete}
        message="Irreversible action!!! Are you sure you want to clear these records?"
      />
      <CustomWarningDialog
        open={Boolean(pendingDeleteComment)}
        setOpen={(val) => { if (!val) setPendingDeleteComment(null); }}
        action={handleDeleteCustomComment}
        message="Are you sure you want to delete this comment field?"
      />
      <Grid item xs={12} lg={11} sx={{ pb: isMobile ? 10 : 0 }}>
        <Stack
          direction="row"
          spacing={1}
          sx={{
            "& .tabItem": {
              padding: "10px",
              cursor: "pointer",
              "&.active": {
                backgroundColor: (theme) => theme.palette.mode === "dark" ? "#2a2a2a" : "white",
                borderLeft: (theme) => `1px solid ${theme.palette.mode === "dark" ? "#555" : "#ccc"}`,
                borderTop: (theme) => `1px solid ${theme.palette.mode === "dark" ? "#555" : "#ccc"}`,
                borderRight: (theme) => `1px solid ${theme.palette.mode === "dark" ? "#555" : "#ccc"}`,
                borderRadius: "5px 5px 0 0",
              },
            },
          }}
        >
          <Box
            className={`tabItem ${activeTab === 0 && " active"}`}
            onClick={() => setActiveTab(0)}
          >
            Term
          </Box>
          <Box
            className={`tabItem ${activeTab === 1 && " active"}`}
            onClick={() => setActiveTab(1)}
          >
            Midterm
          </Box>
        </Stack>

        {activeTab === 0 && (
          <StyledPaper>
            <Box className="paperBody" sx={{ textAlign: "center" }}>
              <Typography variant="formHeading" color="primary.main">
                {student?.first_name + " " + student?.last_name}'s scoresheet
              </Typography>
              <Typography variant="formSubHeading">
                <b>Session:</b>
                {session?.name} | <b>Term:</b> {term?.name} |<b>Class:</b>{" "}
                {classroom?.name}
              </Typography>
            </Box>
            {isMobile && !isCommentBased ? (
              <Accordion defaultExpanded={false} disableGutters elevation={0} sx={{ mb: 1, '&:before': { display: 'none' } }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">Subjects</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                  <Box sx={{ p: 1.5 }}>
                    {(isLoadingClassroom ||
                      isLoadingScores ||
                      isLoadingTerm ||
                      isLoadingSession ||
                      isLoadingSubsection) && <LinearProgress />}
                    {classroom?.subjects?.map((subject) => (
                      <SubjectMobileCard
                        key={subject?.id}
                        subject={subject}
                        classId={classId || 0}
                        student={student || {}}
                        assessments={assessments || []}
                        grades={scoreGrades || []}
                      />
                    ))}
                  </Box>
                </AccordionDetails>
              </Accordion>
            ) : (
              <TableContainer sx={{ overflowX: "auto" }}>
                {(isLoadingClassroom ||
                  isLoadingScores ||
                  isLoadingTerm ||
                  isLoadingSession ||
                  isLoadingSubsection) && <LinearProgress />}
                <Table sx={{ width: "100%", boxShadow: "none" }}>
                  <TableHead>
                    <TableRow>
                      <StyledTableCell>Subject</StyledTableCell>
                      {isCommentBased ? (
                        <StyledTableCell>Comment</StyledTableCell>
                      ) : (
                        <>
                          {assessments?.map((assessment) => (
                            <StyledTableCell
                              key={assessment.id}
                            >{`${assessment.name} (${assessment.obtainable_score})`}</StyledTableCell>
                          ))}
                          <StyledTableCell>Total marks</StyledTableCell>
                          <StyledTableCell>grades</StyledTableCell>
                          <StyledTableCell>remarks</StyledTableCell>
                        </>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {classroom?.subjects?.map((subject) => (
                      <SubjectTableRow
                        key={subject?.id}
                        subject={subject}
                        classId={classId || 0}
                        student={student || {}}
                        assessments={assessments || []}
                        grades={scoreGrades || []}
                        isCommentBased={isCommentBased}
                      />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            <Box sx={{ textAlign: "left", p: 2 }}>
              {isLoadingComment && <LinearProgress />}

              {/* Editable: teacher's fields first */}
              {!isAnyHead && (
                <>
                  <FormControl fullWidth sx={{ my: 4 }}>
                    <Typography variant="formSubHeading">
                      Classroom comment
                    </Typography>
                    <StyledTextArea
                      minRows={4}
                      value={isLoadingComment ? "" : teacherComment}
                      onChange={(e) => setTeacherComment(e.target.value)}
                      disabled={isAnyHead}
                    />
                  </FormControl>
                  <Box sx={{ mt: 3 }}>
                    {customTermComments.map((c, i) => (
                      <Box key={i} sx={{ mt: 3 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                          <StyledTextField
                            value={c.title}
                            placeholder="Comment title"
                            fullWidth
                            inputProps={{ readOnly: isAnyHead }}
                            onChange={(e) => {
                              if (isAnyHead) return;
                              const updated = [...customTermComments];
                              updated[i] = { ...updated[i], title: e.target.value };
                              setCustomTermComments(updated);
                              persistCustomCommentTitles(classId, updated, "term");
                            }}
                          />
                          <IconButton
                            size="small"
                            onClick={() => setPendingDeleteComment({ type: 'term', idx: i })}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                        <FormControl fullWidth>
                          <StyledTextArea
                            minRows={3}
                            value={c.content}
                            readOnly={isAnyHead}
                            onChange={(e) => {
                              if (isAnyHead) return;
                              const updated = [...customTermComments];
                              updated[i] = { ...updated[i], content: e.target.value };
                              setCustomTermComments(updated);
                            }}
                          />
                        </FormControl>
                      </Box>
                    ))}
                    <Button
                      startIcon={<AddIcon />}
                      onClick={() =>
                        setCustomTermComments([
                          ...customTermComments,
                          { title: "", content: "" },
                        ])
                      }
                      sx={{ mt: 2 }}
                    >
                      Add comment field
                    </Button>
                  </Box>
                </>
              )}

              {/* Editable: section head's field first */}
              {isSectionHead && (
                <FormControl fullWidth sx={{ my: 4 }}>
                  <Typography variant="formSubHeading">
                    {sectionCommentTitle}
                  </Typography>
                  <StyledTextArea
                    value={principalComment}
                    onChange={(e) => setPrincipalComment(e.target.value)}
                    disabled={!isSectionHead}
                    minRows={4}
                  />
                </FormControl>
              )}

              {/* Editable: dept head's own dept first */}
              {isDepartmentHead && allDepartments?.filter((dept) => parseInt(departmentId) === dept.id).map((dept) => (
                <FormControl key={dept.id} fullWidth sx={{ my: 4 }}>
                  <Typography variant="formSubHeading">
                    {deptCommentTitle(dept)}
                  </Typography>
                  <StyledTextArea
                    value={departmentComments[dept.id] || ""}
                    onChange={(e) =>
                      setDepartmentComments((prev) => ({ ...prev, [dept.id]: e.target.value }))
                    }
                    disabled={!(isDepartmentHead && parseInt(departmentId) === dept.id)}
                    minRows={4}
                  />
                </FormControl>
              ))}

              {/* Readonly: section comment (for non-section-heads) */}
              {!isSectionHead && (
                <FormControl fullWidth sx={{ my: 4 }}>
                  <Typography variant="formSubHeading">
                    {sectionCommentTitle}
                  </Typography>
                  <StyledTextArea
                    value={principalComment}
                    onChange={(e) => setPrincipalComment(e.target.value)}
                    disabled={!isSectionHead}
                    minRows={4}
                  />
                </FormControl>
              )}

              {/* Readonly: dept comments (all except own for dept head) */}
              {allDepartments?.filter((dept) => !(isDepartmentHead && parseInt(departmentId) === dept.id)).map((dept) => (
                <FormControl key={dept.id} fullWidth sx={{ my: 4 }}>
                  <Typography variant="formSubHeading">
                    {deptCommentTitle(dept)}
                  </Typography>
                  <StyledTextArea
                    value={departmentComments[dept.id] || ""}
                    onChange={(e) =>
                      setDepartmentComments((prev) => ({ ...prev, [dept.id]: e.target.value }))
                    }
                    disabled={!(isDepartmentHead && parseInt(departmentId) === dept.id)}
                    minRows={4}
                  />
                </FormControl>
              ))}

              {/* Readonly: classroom + custom (for heads) */}
              {isAnyHead && (
                <>
                  <FormControl fullWidth sx={{ my: 4 }}>
                    <Typography variant="formSubHeading">
                      Classroom comment
                    </Typography>
                    <StyledTextArea
                      minRows={4}
                      value={isLoadingComment ? "" : teacherComment}
                      onChange={(e) => setTeacherComment(e.target.value)}
                      disabled={isAnyHead}
                    />
                  </FormControl>
                  <Box sx={{ mt: 3 }}>
                    {customTermComments.map((c, i) => (
                      <Box key={i} sx={{ mt: 3 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                          <StyledTextField
                            value={c.title}
                            placeholder="Comment title"
                            fullWidth
                            inputProps={{ readOnly: isAnyHead }}
                            onChange={(e) => {
                              if (isAnyHead) return;
                              const updated = [...customTermComments];
                              updated[i] = { ...updated[i], title: e.target.value };
                              setCustomTermComments(updated);
                              persistCustomCommentTitles(classId, updated, "term");
                            }}
                          />
                        </Box>
                        <FormControl fullWidth>
                          <StyledTextArea
                            minRows={3}
                            value={c.content}
                            readOnly={isAnyHead}
                            onChange={(e) => {
                              if (isAnyHead) return;
                              const updated = [...customTermComments];
                              updated[i] = { ...updated[i], content: e.target.value };
                              setCustomTermComments(updated);
                            }}
                          />
                        </FormControl>
                      </Box>
                    ))}
                  </Box>
                </>
              )}
            </Box>
            <Typography variant="formHeading" sx={{ textAlign: "center" }}>
              Traits
            </Typography>
            {(isLoadingCommentGrades ||
              isLoadingTraits ||
              isLoadingStudentTraits) && <LinearProgress />}

            {/* Grade key — collapsed accordion on mobile, full table on desktop */}
            {isMobile ? (
              <Accordion defaultExpanded={false} disableGutters elevation={0} sx={{ mb: 2, '&:before': { display: 'none' }, border: '1px solid', borderColor: 'divider', borderRadius: '8px !important' }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">Grade Key</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                  <TableContainer>
                    <Table size="small" sx={{ boxShadow: "none" }}>
                      <TableHead>
                        <TableRow>
                          <StyledTableCell>Key</StyledTableCell>
                          <StyledTableCell>Description</StyledTableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {commentGrades?.map((row) => (
                          <TableRow key={row.id}>
                            <StyledTableCell>{row.name}</StyledTableCell>
                            <StyledTableCell>{row.description}</StyledTableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            ) : (
              <TableContainer sx={{ pb: 4, overflowX: "auto" }}>
                <Table sx={{ width: "100%", boxShadow: "none" }}>
                  <TableHead>
                    <TableRow>
                      <StyledTableCell>Key</StyledTableCell>
                      <StyledTableCell align="left">description</StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {commentGrades?.map((row) => (
                      <TableRow key={row.id}>
                        <StyledTableCell component="th" scope="row">
                          {row.name}
                        </StyledTableCell>
                        <StyledTableCell component="th" scope="row">
                          {row.description}
                        </StyledTableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Traits — card layout on mobile, grid layout on desktop */}
            {isMobile ? (
              <Box sx={{ px: 0.5, pb: 2 }}>
                {subsectionTraits?.map((trait) => {
                  const gradedCount = trait?.behaviors?.filter((b) =>
                    traitsRecord?.some((t) => t.trait_id === trait.id && t.behavior_id === b.id && t.grade_id)
                  ).length || 0;
                  const totalCount = trait?.behaviors?.length || 0;
                  const allGraded = gradedCount === totalCount && totalCount > 0;
                  return (
                    <Accordion
                      key={trait.id}
                      defaultExpanded={false}
                      disableGutters
                      elevation={0}
                      sx={{
                        mb: 1.5,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: '8px !important',
                        borderLeft: `4px solid ${trait.color || '#1976d2'}`,
                        overflow: 'hidden',
                        '&:before': { display: 'none' },
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{
                          backgroundColor: trait.color ? `${trait.color}22` : 'action.hover',
                          minHeight: 48,
                          '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1 },
                        }}
                      >
                        <Typography variant="subtitle1" fontWeight={700} sx={{ flexGrow: 1 }}>
                          {trait.name}
                        </Typography>
                        <Chip
                          label={`${gradedCount}/${totalCount}`}
                          size="small"
                          color={allGraded ? "success" : "default"}
                          variant={allGraded ? "filled" : "outlined"}
                          sx={{ mr: 0.5, fontSize: '0.7rem', height: 20 }}
                        />
                      </AccordionSummary>
                      <AccordionDetails sx={{ p: 0 }}>
                        {trait?.behaviors?.map((behavior, bIdx) => {
                          const currentGradeId = traitsRecord?.find(
                            (item) => item.trait_id === trait?.id && item.behavior_id === behavior?.id
                          )?.grade_id || "";
                          return (
                            <Box key={behavior.id}>
                              {bIdx > 0 && <Divider />}
                              <Box sx={{ px: 2, py: 1.5 }}>
                                <Typography variant="body2" sx={{ mb: 1.25, lineHeight: 1.4 }}>
                                  {behavior.name}
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                                  {commentGrades?.map((grade) => {
                                    const isSelected = currentGradeId === grade.id;
                                    return (
                                      <Chip
                                        key={grade.id}
                                        label={grade.name}
                                        size="small"
                                        onClick={() =>
                                          handleTraitGradeChange(
                                            { target: { value: grade.id } },
                                            trait.id,
                                            behavior.id
                                          )
                                        }
                                        color={isSelected ? "primary" : "default"}
                                        variant={isSelected ? "filled" : "outlined"}
                                        sx={{
                                          cursor: 'pointer',
                                          fontWeight: isSelected ? 600 : 400,
                                          minWidth: 48,
                                          '&:hover': { opacity: 0.85 },
                                        }}
                                      />
                                    );
                                  })}
                                </Box>
                              </Box>
                            </Box>
                          );
                        })}
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </Box>
            ) : (
              <TableContainer sx={{ pb: 4 }}>
                <Table sx={{ boxShadow: 'none' }}>
                  <TableHead>
                    <TableRow>
                      <StyledTableCell>Behavior</StyledTableCell>
                      <StyledTableCell sx={{ width: 200 }}>Grade</StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {subsectionTraits?.map((trait) => {
                      const gradedCount = trait?.behaviors?.filter((b) =>
                        traitsRecord?.some((t) => t.trait_id === trait.id && t.behavior_id === b.id && t.grade_id)
                      ).length || 0;
                      const totalCount = trait?.behaviors?.length || 0;
                      const allGraded = gradedCount === totalCount && totalCount > 0;
                      return (
                        <React.Fragment key={trait.id}>
                          {/* Trait section header row */}
                          <TableRow>
                            <StyledTableCell
                              colSpan={2}
                              sx={{
                                backgroundColor: trait.color ? `${trait.color}22` : 'action.hover',
                                borderLeft: `4px solid ${trait.color || '#1976d2'}`,
                                py: 1,
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="subtitle2" fontWeight={700} sx={{ flexGrow: 1 }}>
                                  {trait.name}
                                </Typography>
                                <Chip
                                  label={`${gradedCount}/${totalCount}`}
                                  size="small"
                                  color={allGraded ? "success" : "default"}
                                  variant={allGraded ? "filled" : "outlined"}
                                  sx={{ fontSize: '0.7rem', height: 20 }}
                                />
                              </Box>
                            </StyledTableCell>
                          </TableRow>
                          {/* Behavior rows */}
                          {trait?.behaviors?.map((behavior) => {
                            const currentGradeId = traitsRecord?.find(
                              (item) => item.trait_id === trait?.id && item.behavior_id === behavior?.id
                            )?.grade_id || "";
                            return (
                              <StyledTableRow key={behavior.id}>
                                <StyledTableCell>{behavior.name}</StyledTableCell>
                                <StyledTableCell sx={{ width: 200, py: 0.5 }}>
                                  <FormControl fullWidth size="small">
                                    <StyledSelect
                                      displayEmpty
                                      value={currentGradeId}
                                      onChange={(e) => handleTraitGradeChange(e, trait.id, behavior.id)}
                                    >
                                      <MenuItem value="" disabled>
                                        <em>Select grade</em>
                                      </MenuItem>
                                      {commentGrades?.map((grade) => (
                                        <MenuItem key={grade.id} value={grade.id}>
                                          {grade.name}
                                        </MenuItem>
                                      ))}
                                    </StyledSelect>
                                  </FormControl>
                                </StyledTableCell>
                              </StyledTableRow>
                            );
                          })}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </StyledPaper>
        )}

        {activeTab === 1 && (
          <StyledPaper>
            <Box className="paperBody" sx={{ textAlign: "center" }}>
              <Typography variant="formHeading" color="primary.main">
                {student?.first_name + " " + student?.last_name}'s midterm
              </Typography>
              <Typography variant="formSubHeading">
                <b>Session:</b>
                {session?.name} | <b>Term:</b> {term?.name} |<b>Class:</b>{" "}
                {classroom?.name}
              </Typography>
            </Box>

            {/* Subject teachers' midterm comments */}
            {isMobile ? (
              <Accordion defaultExpanded={false} disableGutters elevation={0} sx={{ mb: 1, '&:before': { display: 'none' } }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">Subjects</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                  <Box sx={{ p: 1.5 }}>
                    {isLoadingMidtermComment && <LinearProgress />}
                    {classroom?.subjects?.map((subject) => (
                      <SubjectMidtermMobileCard
                        key={subject?.id}
                        subject={subject}
                        classId={classId || 0}
                        student={student || {}}
                      />
                    ))}
                  </Box>
                </AccordionDetails>
              </Accordion>
            ) : (
              <TableContainer sx={{ overflowX: "auto" }}>
                {isLoadingMidtermComment && <LinearProgress />}
                <Table sx={{ width: "100%", boxShadow: "none" }}>
                  <TableHead>
                    <TableRow>
                      <StyledTableCell>Subject</StyledTableCell>
                      <StyledTableCell>Midterm remark</StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {classroom?.subjects?.map((subject) => (
                      <SubjectTableRow
                        key={subject?.id}
                        subject={subject}
                        classId={classId || 0}
                        student={student || {}}
                        assessments={[]}
                        grades={[]}
                        type="midterm"
                      />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            <Box sx={{ textAlign: "left", p: 2 }}>
              {isLoadingMidtermComment && <LinearProgress />}

              {/* Editable: teacher's fields first */}
              {!isAnyHead && (
                <>
                  <FormControl fullWidth sx={{ my: 4 }}>
                    <Typography variant="formSubHeading">
                      Classroom midterm comment
                    </Typography>
                    <StyledTextArea
                      minRows={4}
                      value={midtermTeacherComment}
                      onChange={(e) => setMidtermTeacherComment(e.target.value)}
                      readOnly={isAnyHead}
                    />
                  </FormControl>
                  <Box sx={{ mt: 3 }}>
                    {customMidtermComments.map((c, i) => (
                      <Box key={i} sx={{ mt: 3 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                          <StyledTextField
                            value={c.title}
                            placeholder="Comment title"
                            fullWidth
                            inputProps={{ readOnly: isAnyHead }}
                            onChange={(e) => {
                              if (isAnyHead) return;
                              const updated = [...customMidtermComments];
                              updated[i] = { ...updated[i], title: e.target.value };
                              setCustomMidtermComments(updated);
                              persistCustomCommentTitles(classId, updated, "midterm");
                            }}
                          />
                          <IconButton
                            size="small"
                            onClick={() => setPendingDeleteComment({ type: 'midterm', idx: i })}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                        <FormControl fullWidth>
                          <StyledTextArea
                            minRows={3}
                            value={c.content}
                            readOnly={isAnyHead}
                            onChange={(e) => {
                              if (isAnyHead) return;
                              const updated = [...customMidtermComments];
                              updated[i] = { ...updated[i], content: e.target.value };
                              setCustomMidtermComments(updated);
                            }}
                          />
                        </FormControl>
                      </Box>
                    ))}
                    <Button
                      startIcon={<AddIcon />}
                      onClick={() =>
                        setCustomMidtermComments([
                          ...customMidtermComments,
                          { title: "", content: "" },
                        ])
                      }
                      sx={{ mt: 2 }}
                    >
                      Add comment field
                    </Button>
                  </Box>
                </>
              )}

              {/* Editable: section head's field first */}
              {isSectionHead && (
                <FormControl fullWidth sx={{ my: 4 }}>
                  <Typography variant="formSubHeading">
                    {sectionCommentTitle} (Midterm)
                  </Typography>
                  <StyledTextArea
                    value={midtermPrincipalComment}
                    onChange={(e) => setMidtermPrincipalComment(e.target.value)}
                    readOnly={!isSectionHead}
                    minRows={4}
                  />
                </FormControl>
              )}

              {/* Editable: dept head's own dept first */}
              {isDepartmentHead && allDepartments?.filter((dept) => parseInt(departmentId) === dept.id).map((dept) => (
                <FormControl key={dept.id} fullWidth sx={{ my: 4 }}>
                  <Typography variant="formSubHeading">
                    {deptCommentTitle(dept)} (Midterm)
                  </Typography>
                  <StyledTextArea
                    value={midtermDepartmentComments[dept.id] || ""}
                    onChange={(e) =>
                      setMidtermDepartmentComments((prev) => ({ ...prev, [dept.id]: e.target.value }))
                    }
                    readOnly={!(isDepartmentHead && parseInt(departmentId) === dept.id)}
                    minRows={4}
                  />
                </FormControl>
              ))}

              {/* Readonly: section comment (for non-section-heads) */}
              {!isSectionHead && (
                <FormControl fullWidth sx={{ my: 4 }}>
                  <Typography variant="formSubHeading">
                    {sectionCommentTitle} (Midterm)
                  </Typography>
                  <StyledTextArea
                    value={midtermPrincipalComment}
                    onChange={(e) => setMidtermPrincipalComment(e.target.value)}
                    readOnly={!isSectionHead}
                    minRows={4}
                  />
                </FormControl>
              )}

              {/* Readonly: dept comments (all except own for dept head) */}
              {allDepartments?.filter((dept) => !(isDepartmentHead && parseInt(departmentId) === dept.id)).map((dept) => (
                <FormControl key={dept.id} fullWidth sx={{ my: 4 }}>
                  <Typography variant="formSubHeading">
                    {deptCommentTitle(dept)} (Midterm)
                  </Typography>
                  <StyledTextArea
                    value={midtermDepartmentComments[dept.id] || ""}
                    onChange={(e) =>
                      setMidtermDepartmentComments((prev) => ({ ...prev, [dept.id]: e.target.value }))
                    }
                    readOnly={!(isDepartmentHead && parseInt(departmentId) === dept.id)}
                    minRows={4}
                  />
                </FormControl>
              ))}

              {/* Readonly: classroom + custom (for heads) */}
              {isAnyHead && (
                <>
                  <FormControl fullWidth sx={{ my: 4 }}>
                    <Typography variant="formSubHeading">
                      Classroom midterm comment
                    </Typography>
                    <StyledTextArea
                      minRows={4}
                      value={midtermTeacherComment}
                      onChange={(e) => setMidtermTeacherComment(e.target.value)}
                      readOnly={isAnyHead}
                    />
                  </FormControl>
                  <Box sx={{ mt: 3 }}>
                    {customMidtermComments.map((c, i) => (
                      <Box key={i} sx={{ mt: 3 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                          <StyledTextField
                            value={c.title}
                            placeholder="Comment title"
                            fullWidth
                            inputProps={{ readOnly: isAnyHead }}
                            onChange={(e) => {
                              if (isAnyHead) return;
                              const updated = [...customMidtermComments];
                              updated[i] = { ...updated[i], title: e.target.value };
                              setCustomMidtermComments(updated);
                              persistCustomCommentTitles(classId, updated, "midterm");
                            }}
                          />
                        </Box>
                        <FormControl fullWidth>
                          <StyledTextArea
                            minRows={3}
                            value={c.content}
                            readOnly={isAnyHead}
                            onChange={(e) => {
                              if (isAnyHead) return;
                              const updated = [...customMidtermComments];
                              updated[i] = { ...updated[i], content: e.target.value };
                              setCustomMidtermComments(updated);
                            }}
                          />
                        </FormControl>
                      </Box>
                    ))}
                  </Box>
                </>
              )}
            </Box>
          </StyledPaper>
        )}
      </Grid>
      {!isMobile && (
      <Grid item xs={12} lg={1}>
        {/* Template selection popover */}
        <Popover
          open={Boolean(templateAnchorEl)}
          anchorEl={templateAnchorEl}
          onClose={() => setTemplateAnchorEl(null)}
          anchorOrigin={{
            vertical: "center",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "center",
            horizontal: "right",
          }}
        >
          <Box sx={{ p: 1, minWidth: 200 }}>
            <Typography variant="subtitle2" sx={{ px: 1, py: 0.5, fontWeight: 600 }}>
              Select Template
            </Typography>
            <List dense disablePadding>
              {subsection?.templates
                ?.filter((template) => template.type !== "broadsheet")
                ?.map((template) => (
                  <ListItem key={template.id} disablePadding>
                    <ListItemButton
                      onClick={() => {
                        setTemplateAnchorEl(null);
                        setActiveTemplate(template);
                        handlePrint(template);
                      }}
                    >
                      <ListItemText primary={template.name} secondary={template.description} />
                    </ListItemButton>
                  </ListItem>
                ))}
              {subsection?.templates?.filter((t) => t.type !== "broadsheet")?.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                  No report templates available
                </Typography>
              )}
            </List>
          </Box>
        </Popover>
        <Stack
          direction={{ xs: "row", md: "column" }}
          spacing={1}
          sx={{
            "& .btn-icon": {
              fontSize: "14px",
            },
          }}
        >
          {isPrintingReport ? (
            <CircularProgress size={24} />
          ) : (
            <Tooltip placement="left" title="Print Report">
              <FilledButton onClick={(event) => {
                const availableTemplates = subsection?.templates?.filter((t) => t.type !== "broadsheet") || [];
                if (availableTemplates.length === 1) {
                  setActiveTemplate(availableTemplates[0]);
                  handlePrint(availableTemplates[0]);
                } else {
                  setTemplateAnchorEl(event.currentTarget);
                }
              }}>
                <Print className="btn-icon" />
              </FilledButton>
            </Tooltip>
          )}
          {saveTraitsLoading || saveCommentLoading ? (
            <CircularProgress size={24} />
          ) : (
            <Tooltip title="Save" placement="left">
              <FilledButton onClick={handleSave}>
                <SaveIcon className="btn-icon" />
              </FilledButton>
            </Tooltip>
          )}
          <Tooltip title="Generate AI comment" placement="left">
            <FilledButton onClick={() => setOpenAiComment(true)}>
              <AutoAwesomeIcon className="btn-icon" />
            </FilledButton>
          </Tooltip>
          {!isAnyHead ? (
            deleteTraitsLoading || deleteCommentLoading ? (
              <CircularProgress size={24} />
            ) : (
              <Tooltip placement="left" title="Clear record">
                <FilledButton onClick={() => setOpenWarning(true)}>
                  <DeleteIcon className="btn-icon" />
                </FilledButton>
              </Tooltip>
            )
          ) : null}
        </Stack>
      </Grid>
      )}

      {isMobile && (
        <>
          <Popover
            open={Boolean(templateAnchorEl)}
            anchorEl={templateAnchorEl}
            onClose={() => setTemplateAnchorEl(null)}
            anchorOrigin={{
              vertical: "top",
              horizontal: "center",
            }}
            transformOrigin={{
              vertical: "bottom",
              horizontal: "center",
            }}
          >
            <Box sx={{ p: 1, minWidth: 200 }}>
              <Typography variant="subtitle2" sx={{ px: 1, py: 0.5, fontWeight: 600 }}>
                Select Template
              </Typography>
              <List dense disablePadding>
                {subsection?.templates
                  ?.filter((template) => template.type !== "broadsheet")
                  ?.map((template) => (
                    <ListItem key={template.id} disablePadding>
                      <ListItemButton
                        onClick={() => {
                          setTemplateAnchorEl(null);
                          setActiveTemplate(template);
                          handlePrint(template);
                        }}
                      >
                        <ListItemText primary={template.name} secondary={template.description} />
                      </ListItemButton>
                    </ListItem>
                  ))}
                {subsection?.templates?.filter((t) => t.type !== "broadsheet")?.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                    No report templates available
                  </Typography>
                )}
              </List>
            </Box>
          </Popover>

          <Box
            sx={{
              position: "fixed",
              bottom: 0,
              left: 0,
              right: 0,
              zIndex: (zIndex) => zIndex.appBar,
              backgroundColor: "background.paper",
              borderTop: "1px solid",
              borderColor: "divider",
              px: 2,
              py: 1,
            }}
          >
            <Stack
              direction="row"
              spacing={1}
              justifyContent="center"
              sx={{
                "& .btn-icon": {
                  fontSize: "14px",
                },
              }}
            >
              {isPrintingReport ? (
                <CircularProgress size={24} />
              ) : (
                <Tooltip title="Print Report">
                  <FilledButton onClick={(event) => {
                    const availableTemplates = subsection?.templates?.filter((t) => t.type !== "broadsheet") || [];
                    if (availableTemplates.length === 1) {
                      setActiveTemplate(availableTemplates[0]);
                      handlePrint(availableTemplates[0]);
                    } else {
                      setTemplateAnchorEl(event.currentTarget);
                    }
                  }}>
                    <Print className="btn-icon" />
                  </FilledButton>
                </Tooltip>
              )}
              {saveTraitsLoading || saveCommentLoading ? (
                <CircularProgress size={24} />
              ) : (
                <Tooltip title="Save">
                  <FilledButton onClick={handleSave}>
                    <SaveIcon className="btn-icon" />
                  </FilledButton>
                </Tooltip>
              )}
              <Tooltip title="Generate AI comment">
                <FilledButton onClick={() => setOpenAiComment(true)}>
                  <AutoAwesomeIcon className="btn-icon" />
                </FilledButton>
              </Tooltip>
              {!isAnyHead ? (
                deleteTraitsLoading || deleteCommentLoading ? (
                  <CircularProgress size={24} />
                ) : (
                  <Tooltip title="Clear record">
                    <FilledButton onClick={() => setOpenWarning(true)}>
                      <DeleteIcon className="btn-icon" />
                    </FilledButton>
                  </Tooltip>
                )
              ) : null}
            </Stack>
          </Box>
        </>
      )}

      {/* AI Comment Modal */}
      <AICommentModal
        open={openAiComment}
        onClose={() => setOpenAiComment(false)}
        roleType={isDepartmentHead ? "department_head" : isSectionHead ? "section_head" : "classroom_manager"}
        classroomId={classId ? parseInt(classId) : null}
        sectionId={sectionId ? parseInt(sectionId) : null}
        departmentId={departmentId ? parseInt(departmentId) : null}
        commentType={activeTab === 1 ? "midterm" : "term"}
        commentTitle={isDepartmentHead ? deptCommentTitle(allDepartments?.find((d) => d.id === parseInt(departmentId))) : isSectionHead ? sectionCommentTitle : "Classroom Comment"}
        termId={term?.id || null}
        students={student ? [student] : []}
        existingComments={[
          {
            student_id: student?.id,
            content: isDepartmentHead
              ? departmentComments[parseInt(departmentId)]
              : isSectionHead
              ? principalComment
              : teacherComment,
          },
        ]}
        onConfirmed={() => {
          setOpenAiComment(false);
        }}
      />
    </Grid>
  );
}

export default ExamsScoreSheet;
